import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ChefHat, Heart, Sparkles, Plus, Pencil, Trash2, Upload, X, ImageIcon,
  ChevronDown, ChevronRight, Folder, FileText, Utensils, Eye, EyeOff
} from "lucide-react";
import {
  fetchMenuTree,
  createGroup, updateGroup, deleteGroup,
  createSection, updateSection, deleteSection,
  createItem, updateItem, deleteItem,
  uploadImage, uploadMedia, absoluteUrl,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";

// ---------- Small util ----------
const inputCls =
  "w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors font-sans text-sm";

function FlagPill({ active, icon: Icon, label, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-1 text-[0.65rem] uppercase tracking-[0.18em] border ${
        active
          ? "border-primary text-primary bg-primary/10"
          : "border-border text-muted-foreground/60"
      }`}
    >
      <Icon size={10} strokeWidth={1.5} />
      {label}
    </span>
  );
}

// ==============================================================
// Dialog forms — Group / Section / Item
// ==============================================================
function GroupDialog({ open, onOpenChange, initial, onSave }) {
  const [label, setLabel] = useState("");
  const [tagline, setTagline] = useState("");
  const [order, setOrder] = useState(0);
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLabel(initial?.label || "");
      setTagline(initial?.tagline || "");
      setOrder(initial?.order ?? 0);
      setImageUrl(initial?.image_url || "");
      setVideoUrl(initial?.video_url || "");
    }
  }, [open, initial]);

  async function pickMedia(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadMedia(file);
      if (res.media_type === "video") {
        setVideoUrl(res.url || res.image_url);
        setImageUrl("");
      } else {
        setImageUrl(res.url || res.image_url);
        setVideoUrl("");
      }
      toast.success("Uploaded — click Save to apply.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        label,
        tagline,
        order: parseInt(order || 0, 10),
        image_url: imageUrl || null,
        video_url: videoUrl || null,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  const previewUrl = videoUrl || imageUrl;
  const previewIsVideo = !!videoUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="group-dialog"
        className="bg-card border border-border text-foreground rounded-none max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="serif text-3xl font-light">
            {initial ? "Edit group" : "New group"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            Top-level category — e.g. Food, Beverages, Wine.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-8 pt-4">
          <div>
            <label className="overline block mb-3">Label</label>
            <input
              data-testid="group-form-label"
              className={inputCls}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="overline block mb-3">Tagline</label>
            <input
              data-testid="group-form-tagline"
              className={inputCls}
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="Optional descriptor shown under the tab"
            />
          </div>
          <div>
            <label className="overline block mb-3">Order</label>
            <input
              data-testid="group-form-order"
              type="number"
              className={inputCls}
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            />
          </div>

          <div className="pt-4 border-t border-border">
            <div className="overline mb-2">Home teaser background</div>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Optional image OR video shown on the home page &ldquo;On The Menu&rdquo; card for this group.
              Uploading either one replaces the other.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              data-testid="group-form-media-input"
              onChange={(e) => pickMedia(e.target.files?.[0])}
            />
            <div className="flex items-center gap-4">
              <div
                className="w-28 h-20 border border-border bg-background/60 flex items-center justify-center overflow-hidden"
                data-testid="group-form-media-preview"
              >
                {previewUrl ? (
                  previewIsVideo ? (
                    <video
                      src={absoluteUrl(previewUrl)}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      autoPlay
                      loop
                    />
                  ) : (
                    <img
                      src={absoluteUrl(previewUrl)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <ImageIcon size={20} strokeWidth={1.5} className="text-muted-foreground/50" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  data-testid="group-form-media-upload"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                >
                  <Upload size={12} strokeWidth={1.5} />
                  {uploading ? "Uploading…" : previewUrl ? "Replace" : "Upload image or video"}
                </button>
                {previewUrl && (
                  <button
                    type="button"
                    data-testid="group-form-media-remove"
                    onClick={() => { setImageUrl(""); setVideoUrl(""); }}
                    className="inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X size={11} strokeWidth={1.5} />
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              className="px-6 py-3 text-[0.72rem] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
            <button
              data-testid="group-form-submit"
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SectionDialog({ open, onOpenChange, initial, groupId, onSave }) {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [order, setOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || "");
      setSubtitle(initial?.subtitle || "");
      setOrder(initial?.order ?? 0);
    }
  }, [open, initial]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        group_id: groupId,
        title,
        subtitle,
        order: parseInt(order || 0, 10),
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="section-dialog"
        className="bg-card border border-border text-foreground rounded-none max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="serif text-3xl font-light">
            {initial ? "Edit section" : "New section"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            A section groups related menu items — e.g. Hoppers, Sparkling.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-8 pt-4">
          <div>
            <label className="overline block mb-3">Title</label>
            <input
              data-testid="section-form-title"
              className={inputCls}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="overline block mb-3">Subtitle</label>
            <input
              data-testid="section-form-subtitle"
              className={inputCls}
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Italic line above the item list"
            />
          </div>
          <div>
            <label className="overline block mb-3">Order</label>
            <input
              data-testid="section-form-order"
              type="number"
              className={inputCls}
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              className="px-6 py-3 text-[0.72rem] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
            <button
              data-testid="section-form-submit"
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ItemDialog({ open, onOpenChange, initial, sectionId, onSave }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [order, setOrder] = useState(0);
  const [chef, setChef] = useState(false);
  const [fav, setFav] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [images, setImages] = useState([]); // list of URLs
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setDesc(initial?.desc || "");
      setPrice(initial?.price || "");
      setOrder(initial?.order ?? 0);
      setChef(!!initial?.chef_recommended);
      setFav(!!initial?.favorite);
      setIsNew(!!initial?.is_new);
      // Backwards compat: prefer `images`, fall back to legacy `image_url`
      const initialImages =
        (initial?.images && initial.images.length > 0)
          ? initial.images
          : (initial?.image_url ? [initial.image_url] : []);
      setImages(initialImages);
      setUploadProgress(0);
    }
  }, [open, initial]);

  async function handleFile(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    for (const file of files) {
      if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
        toast.error(`${file.name}: use PNG, JPG, WEBP, or GIF.`);
        continue;
      }
      if (file.size > 6 * 1024 * 1024) {
        toast.error(`${file.name}: max size is 6 MB.`);
        continue;
      }
      setUploading(true);
      setUploadProgress(0);
      try {
        const res = await uploadImage(file, (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        });
        setImages((imgs) => [...imgs, res.image_url]);
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Upload failed for ${file.name}`);
      } finally {
        setUploading(false);
      }
    }
    // reset file input so re-selecting same file triggers change
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const removeImage = (idx) =>
    setImages((imgs) => imgs.filter((_, i) => i !== idx));

  const moveImage = (idx, dir) =>
    setImages((imgs) => {
      const target = idx + dir;
      if (target < 0 || target >= imgs.length) return imgs;
      const next = [...imgs];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        section_id: sectionId,
        name,
        desc,
        price,
        order: parseInt(order || 0, 10),
        chef_recommended: chef,
        favorite: fav,
        is_new: isNew,
        images,
        image_url: images[0] || null,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="item-dialog"
        className="bg-card border border-border text-foreground rounded-none max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="serif text-3xl font-light">
            {initial ? "Edit item" : "New item"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            A menu item — name, description, price and flags.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6 pt-4">
          <div>
            <label className="overline block mb-3">Name</label>
            <input
              data-testid="item-form-name"
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="overline block mb-3">Description</label>
            <textarea
              data-testid="item-form-desc"
              rows={3}
              className={inputCls + " resize-none"}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-3">Price</label>
              <input
                data-testid="item-form-price"
                className={inputCls}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 128  or  125 / 680"
                required
              />
            </div>
            <div>
              <label className="overline block mb-3">Order</label>
              <input
                data-testid="item-form-order"
                type="number"
                className={inputCls}
                value={order}
                onChange={(e) => setOrder(e.target.value)}
              />
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <div className="overline mb-3">Image</div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              data-testid="item-form-image-input"
              onChange={(e) => handleFile(e.target.files)}
            />
            {images.length > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="item-form-image-gallery">
                  {images.map((url, idx) => (
                    <div
                      key={`${url}-${idx}`}
                      className="relative group aspect-square border border-border overflow-hidden bg-black"
                      data-testid={`item-form-image-thumb-${idx}`}
                    >
                      <img
                        src={absoluteUrl(url)}
                        alt={`Image ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 text-[0.55rem] uppercase tracking-[0.2em] font-semibold px-1.5 py-0.5 bg-primary text-primary-foreground">
                          Cover
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 flex opacity-0 group-hover:opacity-100 transition-opacity bg-background/85 backdrop-blur-sm">
                        <button
                          type="button"
                          data-testid={`item-form-image-up-${idx}`}
                          onClick={() => moveImage(idx, -1)}
                          disabled={idx === 0}
                          className="flex-1 py-1.5 text-[0.6rem] uppercase tracking-[0.18em] text-foreground/85 hover:text-primary disabled:opacity-30"
                          aria-label="Move left"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          data-testid={`item-form-image-down-${idx}`}
                          onClick={() => moveImage(idx, 1)}
                          disabled={idx === images.length - 1}
                          className="flex-1 py-1.5 text-[0.6rem] uppercase tracking-[0.18em] text-foreground/85 hover:text-primary disabled:opacity-30"
                          aria-label="Move right"
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          data-testid={`item-form-image-remove-${idx}`}
                          onClick={() => removeImage(idx)}
                          className="flex-1 py-1.5 text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground hover:text-destructive"
                          aria-label="Remove"
                        >
                          <X size={11} strokeWidth={1.5} className="inline" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    data-testid="item-form-image-add-more"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                  >
                    <Upload size={11} strokeWidth={1.5} />
                    {uploading ? `Uploading ${uploadProgress}%` : "Add more"}
                  </button>
                  <span className="text-[0.62rem] text-muted-foreground italic">
                    First image is the cover · hover to reorder or remove
                  </span>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="item-form-image-upload"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-3 border border-dashed border-border py-8 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Upload size={14} strokeWidth={1.5} className="animate-pulse" />
                    Uploading {uploadProgress}%
                  </>
                ) : (
                  <>
                    <ImageIcon size={14} strokeWidth={1.5} />
                    Add images · PNG · JPG · WEBP · GIF · Max 6MB each
                  </>
                )}
              </button>
            )}
          </div>

          <div className="pt-4 border-t border-border space-y-4">
            <div className="overline">Flags</div>
            {[
              { key: "chef", label: "Chef's Recommendation", icon: ChefHat, val: chef, set: setChef, tid: "item-form-chef" },
              { key: "fav",  label: "Customer Favorite",     icon: Heart,   val: fav,  set: setFav,  tid: "item-form-fav"  },
              { key: "new",  label: "New Menu Item",         icon: Sparkles, val: isNew, set: setIsNew, tid: "item-form-new" },
            ].map(({ key, label, icon: Icon, val, set, tid }) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm text-foreground/85">
                  <Icon size={14} strokeWidth={1.5} className={val ? "text-primary" : "text-muted-foreground"} />
                  {label}
                </div>
                <Switch data-testid={tid} checked={val} onCheckedChange={set} />
              </div>
            ))}
          </div>

          <DialogFooter>
            <button
              type="button"
              className="px-6 py-3 text-[0.72rem] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
            <button
              data-testid="item-form-submit"
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDelete({ open, onOpenChange, title, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-testid="delete-dialog"
        className="bg-card border border-border rounded-none text-foreground"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="serif text-2xl font-light">Delete {title}?</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground text-sm">
            This cannot be undone. Deleting a group or section also removes everything inside it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-border bg-transparent hover:bg-accent text-foreground/80">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="delete-dialog-confirm"
            className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ==============================================================
// Main menu manager
// ==============================================================
export default function AdminMenus() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === "admin";
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Dialogs
  const [groupDlg, setGroupDlg] = useState({ open: false, initial: null });
  const [sectionDlg, setSectionDlg] = useState({ open: false, initial: null, groupId: null });
  const [itemDlg, setItemDlg] = useState({ open: false, initial: null, sectionId: null });
  const [delDlg, setDelDlg] = useState({ open: false, kind: null, id: null, title: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMenuTree({ includeHidden: true });
      setTree(data);
      setActiveGroupId((cur) => cur || data[0]?.id || null);
    } catch (e) {
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Toggle visibility helpers — optimistic update.
  const setVisibility = async (kind, id, next) => {
    // Optimistically update local tree
    setTree((t) => t.map((g) => {
      if (kind === "group" && g.id === id) return { ...g, is_visible: next };
      const sections = g.sections?.map((s) => {
        if (kind === "section" && s.id === id) return { ...s, is_visible: next };
        const items = s.items?.map((it) =>
          kind === "item" && it.id === id ? { ...it, is_visible: next } : it,
        );
        return { ...s, items };
      });
      return { ...g, sections };
    }));
    try {
      if (kind === "group") await updateGroup(id, { is_visible: next });
      if (kind === "section") await updateSection(id, { is_visible: next });
      if (kind === "item") await updateItem(id, { is_visible: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update visibility");
      load(); // revert
    }
  };

  const activeGroup = useMemo(
    () => tree.find((g) => g.id === activeGroupId) || null,
    [tree, activeGroupId]
  );

  const toggleSection = (id) =>
    setExpandedSections((m) => ({ ...m, [id]: !m[id] }));

  // ---- handlers ----
  async function saveGroup(payload) {
    try {
      if (groupDlg.initial) {
        await updateGroup(groupDlg.initial.id, payload);
        toast.success("Group updated");
      } else {
        const g = await createGroup(payload);
        setActiveGroupId(g.id);
        toast.success("Group created");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save group");
      throw e;
    }
  }

  async function saveSection(payload) {
    try {
      if (sectionDlg.initial) {
        await updateSection(sectionDlg.initial.id, payload);
        toast.success("Section updated");
      } else {
        await createSection(payload);
        toast.success("Section created");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save section");
      throw e;
    }
  }

  async function saveItem(payload) {
    try {
      if (itemDlg.initial) {
        await updateItem(itemDlg.initial.id, payload);
        toast.success("Item updated");
      } else {
        await createItem(payload);
        toast.success("Item created");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save item");
      throw e;
    }
  }

  async function doDelete() {
    const { kind, id } = delDlg;
    try {
      if (kind === "group") await deleteGroup(id);
      if (kind === "section") await deleteSection(id);
      if (kind === "item") await deleteItem(id);
      toast.success("Deleted");
      setDelDlg({ open: false, kind: null, id: null, title: "" });
      if (kind === "group" && activeGroupId === id) setActiveGroupId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  }

  async function toggleFlag(item, key) {
    try {
      await updateItem(item.id, { [key]: !item[key] });
      await load();
    } catch {
      toast.error("Update failed");
    }
  }

  return (
    <div data-testid="page-admin-menus" className="min-h-screen bg-background text-foreground">
      <main className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14">
        {/* Header row */}
        <div className="flex items-end justify-between gap-8 mb-12">
          <div>
            <div className="overline">Menu Manager</div>
            <h1 className="mt-4 serif text-4xl md:text-5xl font-light leading-tight text-foreground">
              Manage the menus.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
              Groups → Sections → Items. Toggle Chef's Rec, Favorite, and New instantly.
            </p>
          </div>
          <button
            data-testid="admin-add-group-btn"
            onClick={() => setGroupDlg({ open: true, initial: null })}
            className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 transition-colors"
          >
            <Plus size={14} strokeWidth={1.5} />
            New Group
          </button>
        </div>

        {loading ? (
          <div data-testid="admin-loading" className="py-24 text-center text-muted-foreground italic serif text-xl">
            <span className="ornament">Loading menu</span>
          </div>
        ) : tree.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <Folder size={28} className="mx-auto text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              No groups yet. Create your first group to get started.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Groups sidebar */}
            <aside className="lg:col-span-3">
              <div className="border border-border bg-card">
                <div className="px-4 py-3 border-b border-border overline flex items-center gap-2">
                  <Folder size={12} strokeWidth={1.5} /> Groups
                </div>
                <ul>
                  {tree.map((g) => (
                    <li
                      key={g.id}
                      data-testid={`admin-group-row-${g.id}`}
                      className={`group flex items-center justify-between px-4 py-3 border-b border-border last:border-b-0 cursor-pointer transition-colors ${
                        g.id === activeGroupId ? "bg-primary/10 text-primary" : "hover:bg-accent"
                      }`}
                      onClick={() => setActiveGroupId(g.id)}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`serif text-lg truncate ${g.is_visible === false ? "opacity-40 line-through" : ""}`}>{g.label}</span>
                        <span className="text-xs text-muted-foreground">
                          ({g.sections.length})
                        </span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          data-testid={`admin-group-visibility-${g.id}`}
                          onClick={(e) => { e.stopPropagation(); setVisibility("group", g.id, !(g.is_visible !== false)); }}
                          className={`p-1.5 transition-colors ${
                            g.is_visible === false
                              ? "text-muted-foreground hover:text-primary"
                              : "text-primary/85 hover:text-primary"
                          }`}
                          title={g.is_visible === false ? "Hidden — click to show" : "Visible — click to hide"}
                        >
                          {g.is_visible === false ? <EyeOff size={12} strokeWidth={1.5} /> : <Eye size={12} strokeWidth={1.5} />}
                        </button>
                        <button
                          data-testid={`admin-group-edit-${g.id}`}
                          onClick={(e) => { e.stopPropagation(); setGroupDlg({ open: true, initial: g }); }}
                          className="p-1.5 text-muted-foreground hover:text-primary transition-colors"
                          title="Edit group"
                        >
                          <Pencil size={12} strokeWidth={1.5} />
                        </button>
                        {isAdmin && (
                          <button
                            data-testid={`admin-group-delete-${g.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDelDlg({ open: true, kind: "group", id: g.id, title: g.label });
                            }}
                            className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                            title="Delete group"
                          >
                            <Trash2 size={12} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Active group body */}
            <section className="lg:col-span-9 space-y-6">
              {activeGroup ? (
                <>
                  <div className="flex items-end justify-between border-b border-border pb-6">
                    <div>
                      <div className="overline">{activeGroup.label}</div>
                      <div className="mt-2 serif italic text-2xl text-foreground/90">
                        {activeGroup.tagline || <span className="text-muted-foreground/60">No tagline</span>}
                      </div>
                    </div>
                    <button
                      data-testid="admin-add-section-btn"
                      onClick={() => setSectionDlg({ open: true, initial: null, groupId: activeGroup.id })}
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-primary text-primary text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                      <Plus size={12} strokeWidth={1.5} />
                      Section
                    </button>
                  </div>

                  {activeGroup.sections.length === 0 ? (
                    <div className="border border-dashed border-border p-14 text-center">
                      <FileText size={24} className="mx-auto text-muted-foreground" />
                      <p className="mt-3 text-muted-foreground text-sm">No sections yet in {activeGroup.label}.</p>
                    </div>
                  ) : (
                    activeGroup.sections.map((s) => {
                      const expanded = expandedSections[s.id] !== false; // default open
                      return (
                        <div
                          key={s.id}
                          data-testid={`admin-section-${s.id}`}
                          className="border border-border bg-card"
                        >
                          <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4">
                            <button
                              onClick={() => toggleSection(s.id)}
                              className="flex items-center gap-3 text-left"
                              data-testid={`admin-section-toggle-${s.id}`}
                            >
                              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              <div>
                                <div className="serif text-xl text-foreground">{s.title}</div>
                                {s.subtitle && (
                                  <div className="text-xs italic text-muted-foreground mt-0.5">{s.subtitle}</div>
                                )}
                              </div>
                              <span className="ml-3 text-xs text-muted-foreground">
                                {s.items.length} {s.items.length === 1 ? "item" : "items"}
                              </span>
                            </button>
                            <div className="flex items-center gap-2">
                              <button
                                data-testid={`admin-section-add-item-${s.id}`}
                                onClick={() => setItemDlg({ open: true, initial: null, sectionId: s.id })}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-[0.68rem] uppercase tracking-[0.2em] text-primary hover:bg-primary hover:text-primary-foreground border border-primary transition-colors"
                              >
                                <Plus size={11} strokeWidth={1.5} /> Item
                              </button>
                              <button
                                data-testid={`admin-section-visibility-${s.id}`}
                                onClick={() => setVisibility("section", s.id, !(s.is_visible !== false))}
                                className={`p-2 transition-colors ${
                                  s.is_visible === false
                                    ? "text-muted-foreground hover:text-primary"
                                    : "text-primary/85 hover:text-primary"
                                }`}
                                title={s.is_visible === false ? "Hidden — click to show" : "Visible — click to hide"}
                              >
                                {s.is_visible === false ? <EyeOff size={12} strokeWidth={1.5} /> : <Eye size={12} strokeWidth={1.5} />}
                              </button>
                              <button
                                data-testid={`admin-section-edit-${s.id}`}
                                onClick={() => setSectionDlg({ open: true, initial: s, groupId: activeGroup.id })}
                                className="p-2 text-muted-foreground hover:text-primary transition-colors"
                                title="Edit section"
                              >
                                <Pencil size={12} strokeWidth={1.5} />
                              </button>
                              {isAdmin && (
                                <button
                                  data-testid={`admin-section-delete-${s.id}`}
                                  onClick={() =>
                                    setDelDlg({ open: true, kind: "section", id: s.id, title: s.title })
                                  }
                                  className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                                  title="Delete section"
                                >
                                  <Trash2 size={12} strokeWidth={1.5} />
                                </button>
                              )}
                            </div>
                          </div>

                          {expanded && (
                            <ul>
                              {s.items.length === 0 ? (
                                <li className="px-5 py-8 text-center text-sm text-muted-foreground">
                                  No items — add one to begin.
                                </li>
                              ) : (
                                s.items.map((it) => (
                                  <li
                                    key={it.id}
                                    data-testid={`admin-item-${it.id}`}
                                    className="px-5 py-4 border-b border-border last:border-b-0 grid grid-cols-[auto_1fr_auto] gap-4 items-start hover:bg-accent/40 transition-colors"
                                  >
                                    <div className="w-14 h-14 shrink-0 border border-border overflow-hidden bg-black/40 flex items-center justify-center">
                                      {it.image_url ? (
                                        <img
                                          src={absoluteUrl(it.image_url)}
                                          alt=""
                                          className="w-full h-full object-cover"
                                          data-testid={`admin-item-thumb-${it.id}`}
                                        />
                                      ) : (
                                        <ImageIcon size={14} strokeWidth={1.5} className="text-muted-foreground/40" />
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                        <span className="serif text-lg text-foreground">{it.name}</span>
                                        <span className="text-primary text-sm tabular-nums">{it.price}</span>
                                      </div>
                                      {it.desc && (
                                        <div className="mt-1 text-xs text-muted-foreground leading-relaxed max-w-2xl">
                                          {it.desc}
                                        </div>
                                      )}
                                      <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <button
                                          data-testid={`admin-item-flag-chef-${it.id}`}
                                          onClick={() => toggleFlag(it, "chef_recommended")}
                                          className="focus:outline-none"
                                          title="Toggle Chef's Recommendation"
                                        >
                                          <FlagPill active={it.chef_recommended} icon={ChefHat} label="Chef" testid={`item-flag-chef-${it.id}`} />
                                        </button>
                                        <button
                                          data-testid={`admin-item-flag-fav-${it.id}`}
                                          onClick={() => toggleFlag(it, "favorite")}
                                          className="focus:outline-none"
                                          title="Toggle Favorite"
                                        >
                                          <FlagPill active={it.favorite} icon={Heart} label="Favorite" testid={`item-flag-fav-${it.id}`} />
                                        </button>
                                        <button
                                          data-testid={`admin-item-flag-new-${it.id}`}
                                          onClick={() => toggleFlag(it, "is_new")}
                                          className="focus:outline-none"
                                          title="Toggle New"
                                        >
                                          <FlagPill active={it.is_new} icon={Sparkles} label="New" testid={`item-flag-new-${it.id}`} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        data-testid={`admin-item-visibility-${it.id}`}
                                        onClick={() => setVisibility("item", it.id, !(it.is_visible !== false))}
                                        className={`p-2 transition-colors ${
                                          it.is_visible === false
                                            ? "text-muted-foreground hover:text-primary"
                                            : "text-primary/85 hover:text-primary"
                                        }`}
                                        title={it.is_visible === false ? "Hidden — click to show" : "Visible — click to hide"}
                                      >
                                        {it.is_visible === false ? <EyeOff size={12} strokeWidth={1.5} /> : <Eye size={12} strokeWidth={1.5} />}
                                      </button>
                                      <button
                                        data-testid={`admin-item-edit-${it.id}`}
                                        onClick={() => setItemDlg({ open: true, initial: it, sectionId: s.id })}
                                        className="p-2 text-muted-foreground hover:text-primary transition-colors"
                                        title="Edit"
                                      >
                                        <Pencil size={12} strokeWidth={1.5} />
                                      </button>
                                      {isAdmin && (
                                        <button
                                          data-testid={`admin-item-delete-${it.id}`}
                                          onClick={() =>
                                            setDelDlg({ open: true, kind: "item", id: it.id, title: it.name })
                                          }
                                          className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                                          title="Delete"
                                        >
                                          <Trash2 size={12} strokeWidth={1.5} />
                                        </button>
                                      )}
                                    </div>
                                  </li>
                                ))
                              )}
                            </ul>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              ) : (
                <div className="border border-dashed border-border p-16 text-center">
                  <Utensils size={28} className="mx-auto text-muted-foreground" />
                  <p className="mt-4 text-muted-foreground">Select a group from the left.</p>
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <GroupDialog
        open={groupDlg.open}
        onOpenChange={(v) => setGroupDlg((s) => ({ ...s, open: v }))}
        initial={groupDlg.initial}
        onSave={saveGroup}
      />
      <SectionDialog
        open={sectionDlg.open}
        onOpenChange={(v) => setSectionDlg((s) => ({ ...s, open: v }))}
        initial={sectionDlg.initial}
        groupId={sectionDlg.groupId}
        onSave={saveSection}
      />
      <ItemDialog
        open={itemDlg.open}
        onOpenChange={(v) => setItemDlg((s) => ({ ...s, open: v }))}
        initial={itemDlg.initial}
        sectionId={itemDlg.sectionId}
        onSave={saveItem}
      />
      <ConfirmDelete
        open={delDlg.open}
        onOpenChange={(v) => setDelDlg((s) => ({ ...s, open: v }))}
        title={delDlg.title}
        onConfirm={doDelete}
      />
    </div>
  );
}
